import moment from 'moment';
import T from 'Components/Helpers/translate';
import { numberFormat } from 'App/babel/components/Filters/Currencies';

/**
 * Renders a prospecting session filter's value as one line of text.
 *
 * Read off the value's runtime shape rather than off the registry's `valueType`:
 * the registry lives in base/ and the browser cannot import it. Every shape the
 * filter tool accepts is distinguishable without it, and an unknown shape falls
 * back to JSON — a filter type added later still renders something.
 */

type Range = { start?: number | null; end?: number | null; years?: number };
type DateRange = { from?: string | null; to?: string | null };
type Keywords = { terms?: unknown; match?: unknown };

/** The enum tokens the tool's choice-valued filters use. */
const CHOICE_KEYS: Record<string, string> = {
	all: 'default.all',
	headquarters: 'soliditet.headquarters',
	branches: 'account.branches',
	notInUpsales: 'default.notInUpsales'
};

const isObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

/** An open-ended range reads as a bound — "From 20" — rather than a dangling dash. */
function bounded(start: string | null, end: string | null): string {
	if (start !== null && end !== null) return `${start}–${end}`;
	if (start !== null) return T('prospecting.v2.value.from', { value: start });
	if (end !== null) return T('prospecting.v2.value.upTo', { value: end });
	return '';
}

function formatDate(value: string): string {
	const parsed = moment(value);
	return parsed.isValid() ? parsed.format('L') : value;
}

/**
 * `labelItem` names the members of a picked list. Only the caller knows what the
 * codes in it mean — industry's names come from the market's classification, and
 * resolving which one applies needs the catalogue's index column (WEB-10109). A
 * caller without that renders the stored value, as every caller used to.
 */
export function formatProspectingFilterValue(value: unknown, labelItem?: (item: string) => string): string {
	if (value == null || value === '') return '';
	if (typeof value === 'string') {
		// Choice filters carry one of a handful of enum tokens, so they need
		// translating; anything else is the rep's own text, shown verbatim.
		const choiceKey = CHOICE_KEYS[value];
		return choiceKey ? T(choiceKey) : value;
	}
	if (typeof value === 'number') return numberFormat(value);
	if (Array.isArray(value)) return value.map(item => (labelItem ?? String)(String(item))).join(', ');
	if (!isObject(value)) return String(value);

	if ('terms' in value) {
		const { terms, match } = value as Keywords;
		const list = Array.isArray(terms) ? terms.map(term => String(term)).filter(Boolean) : [];
		if (!list.length) return '';
		const separator =
			match === 'any' ? T('prospecting.v2.value.keywordsAny') : T('prospecting.v2.value.keywordsAll');
		return list.join(` ${separator} `);
	}

	if ('from' in value || 'to' in value) {
		const { from, to } = value as DateRange;
		return bounded(from ? formatDate(from) : null, to ? formatDate(to) : null);
	}

	if ('start' in value || 'end' in value) {
		const { start, end, years } = value as Range;
		const text = bounded(start != null ? numberFormat(start) : null, end != null ? numberFormat(end) : null);
		if (!text) return '';
		// A change range is a percentage over a window, so both have to show —
		// "20–200" alone would read as a headcount.
		return years ? T('prospecting.v2.value.percentOverYears', { range: text, years }) : text;
	}

	return JSON.stringify(value);
}
